import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import localStorageSlim from 'localstorage-slim';
import JSZip from 'jszip';
import Sdk from 'casdoor-js-sdk';
import { saveAs } from 'file-saver';
import axios from "axios";
import env from '../config/env';
import { jsPDF } from "jspdf";
const dayjs = require('dayjs'); // Import dayjs
const utc = require('dayjs/plugin/utc'); // Import utc plugin
dayjs.extend(utc); // Extend dayjs with utc support
import { signOut } from "next-auth/react";

// Shared TTL for every config-list cache below (contact types, document
// types, states, country codes, transaction statuses, transaction phases,
// contact fields). Tuned to balance freshness against API load: short
// enough that backend rename / addition rolls out within a working day,
// long enough that we don't re-hit /config endpoints on every navigation.
// Used by getContactTypesCached, getContactFieldsCached,
// getDocumentTypesCached, getStatesCached, getCountryCodesCached,
// getTransactionStatusesCached, getTransactionPhasesCached.
const CACHED_DATA_TTL_SECONDS = 6 * 60 * 60; // 6 hours

export const handleFileChange = async(file, setFile, maxPages, setError, setContainerExpanded) => {
    if (file && file.type === 'application/pdf') {
        try {
            await validatePDF(file, maxPages);
            setFile(file);
            setError('');
            setContainerExpanded(false)
        } catch (error) {
            setError(error.message);
            setFile(null);
        }
    } else {
        setError('Please select a valid PDF file.');
    }
};

export const validatePDF = async(file, maxPages) => {
    const arrayBuffer = await file.arrayBuffer();
    let totalPages;
    try {
        const pdf = await PDFDocument.load(arrayBuffer);
        totalPages = pdf.getPageCount();
    } catch (err) {
        // pdf-lib is strict: it refuses ANY /Encrypt dict — including
        // "owner-locked" PDFs from e-sign platforms (Authentisign, DocuSign…)
        // that encrypt with an EMPTY user password and open everywhere
        // without a prompt. Let pdf.js arbitrate before rejecting.
        totalPages = await countPagesInEncryptedPdf(arrayBuffer);
    }
    if (totalPages > maxPages) {
        throw new Error(`The document has more than ${maxPages} pages, kindly check and upload only the required information.`);
    }
    // The page count is returned for callers that show it (the new-transaction
    // workspace's page tiles); existing callers ignore it.
    return totalPages;
};

// Distinguish "encrypted but opens with an empty password" (fine — accept)
// from "needs a real password" (reject). pdf.js is the arbiter: it decrypts
// with password '' exactly like desktop viewers do, so only a
// PasswordException means the user genuinely must remove a password. If the
// probe itself can't run (worker unavailable etc.), fall back to a
// structural page count via pdf-lib ignoreEncryption rather than rejecting
// a file every PDF viewer can open.
const countPagesInEncryptedPdf = async(arrayBuffer) => {
    let probeError = null;
    try {
        const reactPdf = await import('react-pdf');
        const pdfjs = reactPdf.pdfjs;
        const src = String(pdfjs.GlobalWorkerOptions.workerSrc || '');
        // react-pdf v9 presets a bare 'pdf.worker.mjs' the browser cannot
        // resolve; anything without a '/' is unusable as a URL — point at
        // the CDN build matching this pdfjs version (same URL our viewers
        // use). Never downgrade a workerSrc some component already set.
        if (src.indexOf('/') === -1) {
            pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
        }
        // pdf.js detaches the buffer it is handed — probe a copy so the
        // pdf-lib fallback below can still read the original.
        const doc = await pdfjs.getDocument({ data: arrayBuffer.slice(0), password: '' }).promise;
        const totalPages = doc.numPages;
        doc.destroy();
        return totalPages;
    } catch (err) {
        probeError = err;
    }
    if (probeError && probeError.name === 'PasswordException') {
        // Even '' will not open it — a real user password is set. This is
        // the only case that deserves the password-protected rejection.
        throw new Error('This PDF is encrypted with a password. Please remove the password and try again.');
    }
    console.warn('PDF empty-password probe failed; using structural page count instead', probeError);
    const pdf = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    return pdf.getPageCount();
};

// Shared limits for every PDF-upload site in the dashboard. Keep these in one
// place so "PDF only · up to N files · M pages each" stays consistent everywhere.
export const MAX_PDF_PAGES = 60;
// Total PDFs a user may attach in one transaction-creation run. Only the
// multi-file uploader on "Start New Transaction" relies on this default —
// every other validatePdfUpload() call site passes an explicit maxFiles.
export const MAX_PDF_FILES = 10;

// The single PDF-upload gate used across the app. Validates a batch of files
// client-side (pdf-lib) BEFORE any S3 upload, so a rejected file never leaves
// the browser:
//   - must be a PDF (by MIME type or .pdf extension),
//   - at most `maxPages` pages each (via validatePDF),
//   - and, when `maxFiles` is set, the TOTAL (existingCount + accepted) is capped.
// Owner-locked (empty-user-password) PDFs are ACCEPTED — only PDFs that
// genuinely require a password to open, or unreadable files, are rejected.
// Returns { accepted: File[], rejected: string[] } — `rejected` holds
// human-readable reasons ready to show to the user.
export const validatePdfUpload = async(
    fileList, { maxPages = MAX_PDF_PAGES, maxFiles = MAX_PDF_FILES, existingCount = 0 } = {}
) => {
    const files = Array.isArray(fileList) ? fileList : [fileList].filter(Boolean);
    const valid = [];
    const validPages = []; // page count per valid file, same order as `valid`
    const rejected = [];
    for (const file of files) {
        const name = (file && file.name) || 'This file';
        const looksPdf = (file && file.type === 'application/pdf') || /\.pdf$/i.test(name);
        if (!looksPdf) {
            rejected.push(`${name}: only PDF files are allowed.`);
            continue;
        }
        try {
            const pages = await validatePDF(file, maxPages); // throws on > maxPages pages or an unreadable PDF
            valid.push(file);
            validPages.push(typeof pages === 'number' ? pages : null);
        } catch (err) {
            const msg = String((err && err.message) || '').toLowerCase();
            if (msg.includes('more than')) {
                rejected.push(`${name}: exceeds the ${maxPages}-page limit. Please upload only the required pages.`);
            } else if (msg.includes('encrypt')) {
                rejected.push(`${name}: is password-protected. Please remove the password and try again.`);
            } else {
                rejected.push(`${name}: could not be read as a PDF.`);
            }
        }
    }

    // Cap the TOTAL number of accepted PDFs (already-present + newly valid).
    let accepted = valid;
    let pageCounts = validPages;
    if (maxFiles != null) {
        const slots = Math.max(0, maxFiles - existingCount);
        accepted = valid.slice(0, slots);
        pageCounts = validPages.slice(0, slots);
        const overflow = valid.length - accepted.length;
        if (overflow > 0) {
            rejected.push(`You can upload at most ${maxFiles} PDFs — ${overflow} file${overflow === 1 ? '' : 's'} not added.`);
        }
    }

    // pageCounts[i] is accepted[i]'s page count (null when it could not be read).
    return { accepted, rejected, pageCounts };
};


export const handleDrop = async(e, setFile, maxPages, setError, setContainerExpanded) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    await handleFileChange(file, setFile, maxPages, setError, setContainerExpanded);
};

export const handleDragOver = (e) => {
    e.preventDefault();
};

export const logOut = async(authToken) => {

    try {
        await apiCallWithAuth({
            method: 'POST',
            url: '/api/auth/logout',
            headers: {
                'Content-Type': 'application/json',
            },
        }, authToken || 'cookie-auth');
    } catch (e) {
        console.error('Logout API call failed:', e);
    }

    localStorage.clear()
    // sessionStorage previously survived logout (only localStorage was
    // wiped) — clear it too so per-tab markers (e.g. txnChecklistAttach:*)
    // never outlive the session that created them.
    try {
        sessionStorage.clear();
    } catch (e) { /* sessionStorage unavailable — nothing to clear */ }
    signOut({ callbackUrl: "/" });

    // let casdoorConfig = localStorageSlim.get('casdoorConfig', { decrypt: true });

    // if (casdoorConfig) {
    //     const CasdoorSDK = new Sdk(JSON.parse(casdoorConfig));
    //     CasdoorSDK.clearState()
    // }
    // localStorageSlim.remove("token");
    // localStorageSlim.remove("casdoorUserObj");
    // localStorageSlim.remove('accessToken')
    // localStorageSlim.remove('userIdentifier')
    // localStorage.removeItem('userIdentifier')
    // setAuthToken('')
    // // console.log(window.location.origin)
    // window.location.href = window.location.origin
    // window.location.reload()
}

export const apiCallWithAuth = async(options, authToken) => {
    try {

        const response = await axios({
            ...options,
            headers: {
                ...options.headers,
                ...(authToken ? { 'authToken': authToken } : {}),
                'accountId': env.accountId
            }
        });
        return response;
    } catch (error) {
        if (error.response && error.response.status === 403) {
            logOut(authToken)
        } else {
            throw error;
        }
    }
};

// Legacy localStorage key from when this config was cached client-side —
// removed once on load so stale entries don't linger in users' browsers.
const TRANSACTION_COORDINATION_CONFIG_CACHE_KEY = 'transaction_coordination_config_cache';
if (typeof window !== 'undefined') {
    try { localStorage.removeItem(TRANSACTION_COORDINATION_CONFIG_CACHE_KEY); } catch (_) {}
}

/**
 * Fetches transaction coordination config for a state — always a FRESH fetch,
 * no client-side caching, so backend config changes show up immediately.
 * @param {string} state - State code (e.g. 'MN', 'CA')
 * @param {string} authToken - Auth token for API
 * @returns {Promise<{ data: object }>} Same shape as API response (res.data?.data || res.data works)
 */
// Module-level in-flight map keyed by state. When two callers race for the
// same state (typical on the document-checklist page — one fetch for
// transactionSides, another for transactionSubTypes, both firing in
// parallel per state), they share a single network round-trip. Without
// this, both would miss the localStorage cache (nothing there yet) and
// hit the API concurrently. Cleared per-key when the fetch resolves.
const _txnCoordConfigInFlight = {};

export const getTransactionCoordinationConfig = async(state, authToken) => {
    if (!state) {
        return Promise.resolve({ data: {} });
    }
    // Caching commented out per request — every call now goes to the network.
    // In-flight dedup is kept so parallel callers for the same state (e.g.
    // sides + subTypes on the same page) still share one round-trip.
    // if (typeof window !== 'undefined') {
    //     try {
    //         const raw = localStorage.getItem(TRANSACTION_COORDINATION_CONFIG_CACHE_KEY);
    //         const cache = raw ? JSON.parse(raw) : {};
    //         const entry = cache[state];
    //         const now = Date.now();
    //         if (entry && entry.expiresAt > now) {
    //             return Promise.resolve({ data: entry.data });
    //         }
    //     } catch (_) {}
    // }
    if (_txnCoordConfigInFlight[state]) {
        return _txnCoordConfigInFlight[state];
    }
    _txnCoordConfigInFlight[state] = (async () => {
        try {
            const res = await apiCallWithAuth({
                method: 'GET',
                url: `/api/config/transaction-coordination-config?state=${encodeURIComponent(state)}`,
                headers: { 'Content-Type': 'application/json' },
            }, authToken);
            // const responseData = res?.data ?? {};
            // if (typeof window !== 'undefined') {
            //     try {
            //         const raw = localStorage.getItem(TRANSACTION_COORDINATION_CONFIG_CACHE_KEY);
            //         const cache = raw ? JSON.parse(raw) : {};
            //         cache[state] = { data: responseData, expiresAt: Date.now() + TRANSACTION_COORDINATION_CONFIG_TTL_MS };
            //         localStorage.setItem(TRANSACTION_COORDINATION_CONFIG_CACHE_KEY, JSON.stringify(cache));
            //     } catch (_) {}
            // }
            return res;
        } finally {
            delete _txnCoordConfigInFlight[state];
        }
    })();
    return _txnCoordConfigInFlight[state];
};

export const clearTransactionCoordinationConfigCache = () => {
    if (typeof window !== 'undefined') {
        try {
            localStorage.removeItem(TRANSACTION_COORDINATION_CONFIG_CACHE_KEY);
        } catch (_) {}
    }
};

const PAGE_FILTERS_CACHE_KEY = 'page_filters_cache';

export const getPersistedFilters = (pageKey) => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(PAGE_FILTERS_CACHE_KEY);
        if (!raw) return null;
        const allFilters = JSON.parse(raw);
        return allFilters[pageKey] || null;
    } catch (_) {
        return null;
    }
};

export const setPersistedFilters = (pageKey, filters) => {
    if (typeof window === 'undefined') return;
    try {
        const raw = localStorage.getItem(PAGE_FILTERS_CACHE_KEY);
        const allFilters = raw ? JSON.parse(raw) : {};
        allFilters[pageKey] = filters;
        localStorage.setItem(PAGE_FILTERS_CACHE_KEY, JSON.stringify(allFilters));
    } catch (_) {}
};

export const mergePDFs = async(files) => {
    if (files.length == 0) return null;
    if (files.length == 1) return files[0];
    const mergedPdf = await PDFDocument.create();
    for (const file of files) {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => {
            mergedPdf.addPage(page);
        });
    }

    const mergedPdfFile = await mergedPdf.save();
    return new File([mergedPdfFile], 'merged.pdf', { type: 'application/pdf' });
};

export const uploadFile = async(file, authToken) => {
    if (!file) return null
        // const userId = localStorageSlim.get('userIdentifier', { decrypt: true });
        // const userId = localStorage.getItem('userIdentifier');
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiCallWithAuth({
        method: 'POST',
        url: '/api/upload',
        data: formData,
        headers: {
            'Content-Type': 'multipart/form-data',
            'userId': authToken
        }
    }, authToken);
    return response.data.filePath;
}

export const validateEmail = (email) => {
    if (!email || email.includes(',') || email.includes(';') || email.includes(' ')) return false;
    const re = /^\S+@\S+\.\S+$/;
    return re.test(email);
};

export const downloadAllAsZip = async(files, mergedPDFLinkFileName) => {
    const zip = new JSZip();
    // console.log('files: ',files)

    try {
        // Loop over each file and prepare a promise to process it
        const fetchPromises = files.map(async(file) => {
            // Ensure the file.data is properly formatted as a Blob
            const blob = new Blob([file], { type: file.type });
            // Add it to the zip
            if (file.name && file.name.includes('.pdf')) {
                zip.file(`${file.name}`, blob);
            } else {
                zip.file(`${file.name}.pdf`, blob);
            }
        });

        // Wait for all files to be processed and added to the zip
        await Promise.all(fetchPromises);

        // Generate the zip file as a Blob
        const content = await zip.generateAsync({ type: 'blob' });

        // Use FileSaver to save the Blob as a zip file
        saveAs(content, `${mergedPDFLinkFileName}.zip`);
    } catch (error) {
        console.error('Failed to download PDFs:', error);
    }
};

export function generatePDF(text, fileName) {

    if (!text) return null

    const doc = new jsPDF();

    // Define margins and page size
    const marginLeft = 10;
    const marginRight = 10;
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;

    // Define the line height for the breaks
    const lineHeight = 10;

    // Maximum width of the text lines
    const maxWidth = pageWidth - marginLeft - marginRight;

    // Split the text into lines that fit into the page width
    const lines = doc.splitTextToSize(text, maxWidth);

    let yPosition = 20; // Initial vertical position for the first line

    lines.forEach(line => {
        if (yPosition + lineHeight > pageHeight - 20) { // Check if the line fits on the current page
            doc.addPage(); // Add a new page
            yPosition = 20; // Reset y position for the new page
        }

        // Add text line at the calculated position
        doc.text(line, marginLeft, yPosition);
        yPosition += lineHeight; // Move to the next line position
    });

    // Save the PDF or output it in a specific way
    // doc.save('generated.pdf'); // Automatically downloads the PDF

    // Convert the document to a byte array
    const pdfBytes = doc.output('arraybuffer');

    // Return a Blob instead of downloading
    // return new Blob([pdfBytes], { type: 'application/pdf' });

    // Convert the binary blob to a File object
    return new File([pdfBytes], fileName, { type: 'application/pdf' });
}


export const downloadPdf = async(pdfUrl, fileName) => {
    try {
        // console.log('pdfUrl: ',pdfUrl)
        // console.log('fileName: ',fileName)
        const response = await fetch(pdfUrl);
        if (!response.ok) throw new Error('Network response was not ok.');
        const blob = await response.blob();
        saveAs(blob, `${fileName}.pdf`);
    } catch (error) {
        console.error('Failed to download PDF:', error);
    }
};

export const downloadLocalPdf = (pdfBlob, fileName) => {
    try {
        if (pdfBlob instanceof Blob || pdfBlob instanceof File) {
            saveAs(pdfBlob, `${fileName}.pdf`);
        } else {
            throw new Error('Invalid PDF Blob or File object.');
        }
    } catch (error) {
        console.error('Failed to download PDF:', error);
    }
};

export async function downloadCombinedPDF(files) {
    const mergedPdf = await PDFDocument.create();

    // Fetch and load each PDF
    for (let file of files) {
        const existingPdfBytes = await fetch(file.value).then(res => res.arrayBuffer());
        const pdfDoc = await PDFDocument.load(existingPdfBytes);

        // Copy pages from the current PDF to the merged PDF
        const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
        pages.forEach(page => mergedPdf.addPage(page));
    }

    // Serialize the combined PDF document to bytes
    const pdfBytes = await mergedPdf.save();

    // Create a blob and trigger a download
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'merged.pdf';
    link.click();
}


export function encodeUrl(url) {
    // Extract the base URL and filename
    const lastSlashIndex = url.lastIndexOf("/");
    const baseUrl = url.substring(0, lastSlashIndex + 1);
    const filename = url.substring(lastSlashIndex + 1);

    // Encode the filename to handle spaces and special characters
    const encodedFilename = encodeURIComponent(filename);

    // Concatenate the base URL with the encoded filename
    return baseUrl + encodedFilename;
}

export const checkIfEncrypted = async(encryptedPdfBytes) => {
    // Load the PDF as a byte array
    // const pdfPath = '/home/jarvis/Downloads/test.pdf';
    // Try to load the PDF document
    try {
        const pdfDoc = await PDFDocument.load(encryptedPdfBytes);
        // console.log("The PDF is not encrypted.");
        const decryptedPdfBytes = await pdfDoc.save();
        return false
    } catch (error) {
        if (error.message.includes('Invalid PDF structure')) {
            // console.log("The PDF is encrypted.");
        } else {
            // console.error("An error occurred:", error.message);
        }
        return true
    }
}

export const mergePDFsAndHandleIfEncypted = async(files, urls, requestId) => {
    if (files.length == 0 || (files.length == 1 && urls[0] == '')) return null;
    if (files.length == 1) return files[0];

    const mergedPdf = await PDFDocument.create();

    // for (let file of files) {
    for (let [index, file] of files.entries()) {
        const isPdfEncypted = checkIfEncrypted(file)
        if (isPdfEncypted) {
            const apiResponse = await apiCallWithAuth({
                method: 'POST',
                url: '/api/decryptPdf',
                headers: {
                    'Content-Type': 'application/json',
                },
                data: JSON.stringify({
                    requestId: requestId,
                    fileLink: urls[index]
                })
            });

            // const decryptedLink = encodeUrl(apiResponse.data.fileLink);
            // console.log('Normal Url: ',apiResponse.data.fileLink)
            // console.log('decryptedLink Url: ',apiResponse.data.fileLink)
            const response = await axios.get(apiResponse.data.fileLink, { responseType: 'arraybuffer' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const newFile = new File([blob], file.name, { type: 'application/pdf' });
            file = newFile
        }
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => {
            mergedPdf.addPage(page);
        });
    }

    const mergedPdfFile = await mergedPdf.save();
    return new File([mergedPdfFile], 'merged.pdf', { type: 'application/pdf' });
};



export const mergePDFsWithLinks = async(fileLinks) => {
    if (fileLinks.length == 0) return null;
    const mergedPdf = await PDFDocument.create();

    // Add PDFs from fileLinks
    for (const link of fileLinks) {
        const response = await axios.get(link, { responseType: 'arraybuffer' });
        const pdf = await PDFDocument.load(response.data);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach(page => mergedPdf.addPage(page));
    }

    const mergedPdfFile = await mergedPdf.save();
    return new File([mergedPdfFile], 'merged.pdf', { type: 'application/pdf' });
};



export const getFileNameFromURL = (url) => {
    let encodedFileName = url.split('/').pop(); // Get the last segment after the last slash
    let decodedFileName = decodeURIComponent(encodedFileName);
    return decodedFileName;
}

export const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'long', // full name of the month
        day: '2-digit', // two-digit day
        year: 'numeric' // four-digit year
    });
}


export const decodeUrlString = (urlString) => {
    try {
        return decodeURIComponent(urlString);
    } catch (e) {
        console.error('Error decoding URL string:', e);
        return urlString; // Return the original string in case of error
    }
};

export const convertToDayjs = (timestamp) => {
    return dayjs.utc(timestamp);
};


/**
 * Fetches an image from a URL and returns it as a File object.
 *
 * @param {string} imageUrl  The public URL of the image.
 * @returns {Promise<File>}  A promise that resolves to a File.
 */
export async function uploadImageToS3(authToken, setAuthToken, imageUrl) {
    // 1. Fetch the image data
    const res = await fetch(imageUrl);
    if (!res.ok) {
        throw new Error(`Image fetch failed: ${res.status} ${res.statusText}`);
    }

    // 2. Read as a Blob
    const blob = await res.blob();

    // 3. Derive a filename from the URL
    const urlParts = imageUrl.split("/");
    let filename = urlParts[urlParts.length - 1].split("?")[0];
    if (!filename) {
        filename = "downloaded_image";
    }

    // 4. Construct the File
    let imageFile = new File([blob], filename, { type: blob.type })

    // 5. Upload it to S3
    let filePath = await uploadFile(imageFile, authToken, setAuthToken)

    // 6. Return the filePath
    return filePath;
}

/**
 * Fetches contact types from /api/contact-types, caches in localStorageSlim for 6 hours, and returns the result.
 * Uses the cache if available and not expired.
 * @param {string} authToken - The authentication token to use for the API call.
 * @returns {Promise<any>} The contact types response (from cache or API)
 */
// In-flight promise for /api/config/contact-types. Prevents parallel
// mount-time callers from racing to the network before the localStorage
// cache is populated.
let _contactTypesInFlight = null;
export async function getContactTypesCached(authToken) {
    const CACHE_KEY = 'contactTypes';
    const cached = localStorageSlim.get(CACHE_KEY);
    if (cached) return cached;
    if (_contactTypesInFlight) return _contactTypesInFlight;
    _contactTypesInFlight = apiCallWithAuth({
            method: 'GET',
            url: '/api/config/contact-types',
            headers: { 'Content-Type': 'application/json' },
        }, authToken)
        .then((response) => {
            const data = response.data.data;
            localStorageSlim.set(CACHE_KEY, data, { ttl: CACHED_DATA_TTL_SECONDS });
            return data;
        })
        .finally(() => { _contactTypesInFlight = null; });
    return _contactTypesInFlight;
}

// In-flight promise for /api/config/contact-fields. Without this, mount-
// time races (Email + SMS template drawers on the checklist detail page
// both mount at once, each running useTemplateBuilder → each calling
// this helper) fire two identical network round-trips before either can
// populate the localStorage cache.
let _contactFieldsInFlight = null;
export async function getContactFieldsCached(authToken) {
    const CACHE_KEY = 'contactFields';
    const cached = localStorageSlim.get(CACHE_KEY);
    if (cached) {
        return cached;
    }
    if (_contactFieldsInFlight) return _contactFieldsInFlight;
    _contactFieldsInFlight = apiCallWithAuth({
            method: 'GET',
            url: '/api/config/contact-fields',
            headers: { 'Content-Type': 'application/json' },
        }, authToken)
        .then((response) => {
            const data = response.data.data;
            localStorageSlim.set(CACHE_KEY, data, { ttl: CACHED_DATA_TTL_SECONDS });
            return data;
        })
        .finally(() => { _contactFieldsInFlight = null; });
    return _contactFieldsInFlight;
}

/**
 * Format a raw digit string as US phone: (202) 555-0123
 * Stored value stays as raw digits (e.g. "2025550123").
 */
export const formatUSPhone = (value) => {
    if (!value) return '';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

/**
 * Fetches document types from /api/config/document-types, caches in localStorageSlim for 6 hours, and returns the result.
 * Uses the cache if available and not expired.
 * @param {string} authToken - The authentication token to use for the API call.
 * @returns {Promise<any>} The document types response (from cache or API)
 */
let _documentTypesInFlight = null;
export async function getDocumentTypesCached(authToken) {
    const CACHE_KEY = 'documentTypes';
    const cached = localStorageSlim.get(CACHE_KEY);
    if (cached) return cached;
    if (_documentTypesInFlight) return _documentTypesInFlight;
    _documentTypesInFlight = apiCallWithAuth({
            method: 'GET',
            url: '/api/config/document-types',
            headers: { 'Content-Type': 'application/json' },
        }, authToken)
        .then((response) => {
            const data = response.data.data;
            localStorageSlim.set(CACHE_KEY, data, { ttl: CACHED_DATA_TTL_SECONDS });
            return data;
        })
        .finally(() => { _documentTypesInFlight = null; });
    return _documentTypesInFlight;
}

/**
 * Fetches states from /api/config/states, caches in localStorageSlim for 6 hours, and returns the result.
 * Uses the cache if available and not expired.
 * @param {string} authToken - The authentication token to use for the API call.
 * @returns {Promise<any>} The states response (from cache or API)
 */
export async function getStatesCached(authToken) {
    const CACHE_KEY = 'statesList';
    const cached = localStorageSlim.get(CACHE_KEY);
    if (cached) {
        return cached;
    }
    try {
        const response = await apiCallWithAuth({
            method: 'GET',
            url: '/api/config/states-list',
            headers: {
                'Content-Type': 'application/json',
            },
        }, authToken);
        const data = response.data.data;
        localStorageSlim.set(CACHE_KEY, data, { ttl: CACHED_DATA_TTL_SECONDS });
        return data;
    } catch (err) {
        console.log('error: ', err)
        throw err;
    }
}

// Module-level cache + in-flight promise so multiple components (sidebar
// "Create new" menu, transactions list page, etc.) consuming org details
// during the same session share a single network round-trip. Lives in
// memory only — refreshed on full page reload, which is the right cadence
// for org-level config that rarely changes mid-session.
let _orgDetailsCache = null;
let _orgDetailsInFlight = null;
export async function getOrgDetailsCached(authToken) {
    if (_orgDetailsCache) return _orgDetailsCache;
    if (_orgDetailsInFlight) return _orgDetailsInFlight;
    _orgDetailsInFlight = apiCallWithAuth({
            method: 'GET',
            url: '/api/org/details',
            headers: { 'Content-Type': 'application/json' },
        }, authToken)
        .then((response) => {
            _orgDetailsCache = response.data?.data || null;
            return _orgDetailsCache;
        })
        .catch((err) => {
            console.log('error fetching org details: ', err);
            // Don't poison the cache on failure — let the next caller retry.
            throw err;
        })
        .finally(() => {
            _orgDetailsInFlight = null;
        });
    return _orgDetailsInFlight;
}

export function clearOrgDetailsCache() {
    _orgDetailsCache = null;
    _orgDetailsInFlight = null;
}

// Same cache-and-in-flight pattern for /api/user/details. Multiple
// mount-time callers (UserBootstrap in app/providers.js + WidgetLoader
// for widget identity + Navbar's own fetch on with-sidebar layouts)
// share a single network round-trip. In-memory cache, refreshed on full
// page reload.
let _userDetailsCache = null;
let _userDetailsInFlight = null;
export async function getUserDetailsCached(authToken) {
    if (_userDetailsCache) return _userDetailsCache;
    if (_userDetailsInFlight) return _userDetailsInFlight;
    _userDetailsInFlight = apiCallWithAuth({
            method: 'GET',
            url: '/api/user/details',
            headers: { 'Content-Type': 'application/json' },
        }, authToken)
        .then((response) => {
            _userDetailsCache = response.data?.data || null;
            return _userDetailsCache;
        })
        .catch((err) => {
            console.log('error fetching user details: ', err);
            // Don't poison the cache on failure — let the next caller retry.
            throw err;
        })
        .finally(() => {
            _userDetailsInFlight = null;
        });
    return _userDetailsInFlight;
}

export function clearUserDetailsCache() {
    _userDetailsCache = null;
    _userDetailsInFlight = null;
}

// Short-lived cache for /api/agents/list. Compromise between "always
// fresh" and "no repeated calls": the endpoint is called from ~19 pages,
// so every navigation used to fire a fresh 5+ kB response.
//
// TTL choice: 5 minutes. Long enough that navigating between pages in
// normal use is cache-hit; short enough that a teammate who adds an
// agent will surface within 5 minutes on a page navigation. If the
// current user adds/removes an agent themselves, they should call
// clearAgentsListCache() from the create/delete handler to invalidate
// immediately.
//
// In-memory only (NOT localStorage) so it doesn't leak across tabs —
// each browser tab has its own JS execution and thus its own cache. A
// second tab / a fresh tab / any page reload triggers a fresh fetch.
//
// In-flight dedup so concurrent callers (parent page + modal opening in
// the same tick) share one round-trip.
const AGENTS_LIST_TTL_MS = 5 * 60 * 1000;
let _agentsListCache = null;      // { data, expiresAt } | null
let _agentsListInFlight = null;
export async function getAgentsListCached(authToken) {
    if (_agentsListCache && Date.now() < _agentsListCache.expiresAt) {
        return _agentsListCache.data;
    }
    if (_agentsListInFlight) return _agentsListInFlight;
    _agentsListInFlight = apiCallWithAuth({
            method: 'GET',
            url: '/api/agents/list',
            headers: { 'Content-Type': 'application/json' },
        }, authToken)
        .then((response) => {
            const data = response.data?.data || [];
            _agentsListCache = { data, expiresAt: Date.now() + AGENTS_LIST_TTL_MS };
            return data;
        })
        .catch((err) => {
            console.log('error fetching agents list: ', err);
            throw err;
        })
        .finally(() => {
            _agentsListInFlight = null;
        });
    return _agentsListInFlight;
}

// Same list PLUS archived agents (each row carries isArchived). For NAMES and
// filters only — never for a picker that sets a new agent: archived agents are
// shown on what already points at them, never offered for new work.
let _agentsWithArchivedCache = null;      // { data, expiresAt } | null
let _agentsWithArchivedInFlight = null;
export async function getAgentsListWithArchivedCached(authToken) {
    if (_agentsWithArchivedCache && Date.now() < _agentsWithArchivedCache.expiresAt) {
        return _agentsWithArchivedCache.data;
    }
    if (_agentsWithArchivedInFlight) return _agentsWithArchivedInFlight;
    _agentsWithArchivedInFlight = apiCallWithAuth({
            method: 'GET',
            url: '/api/agents/list?includeArchived=true',
            headers: { 'Content-Type': 'application/json' },
        }, authToken)
        .then((response) => {
            const data = (response && response.data && response.data.data) || [];
            _agentsWithArchivedCache = { data, expiresAt: Date.now() + AGENTS_LIST_TTL_MS };
            return data;
        })
        .finally(() => {
            _agentsWithArchivedInFlight = null;
        });
    return _agentsWithArchivedInFlight;
}

export function clearAgentsListCache() {
    _agentsListCache = null;
    _agentsListInFlight = null;
    _agentsWithArchivedCache = null;
    _agentsWithArchivedInFlight = null;
}

// Module-level cache + in-flight promise. Same pattern as
// getOrgDetailsCached above — many pages (agent-configuration, listings,
// checklists, field-template-management, calendar-milestone-configuration,
// document-checklist detail, checklist detail, ReportTemplateEditor)
// call this on every mount. Previously the "Cached" suffix was a lie: the
// helper hit the network on every call. In-memory cache is refreshed on
// full page reload — right cadence for org-level config.
let _orgStatesCache = null;
let _orgStatesInFlight = null;
export async function getOrgStatesCached(authToken) {
    if (_orgStatesCache) return _orgStatesCache;
    if (_orgStatesInFlight) return _orgStatesInFlight;
    _orgStatesInFlight = apiCallWithAuth({
            method: 'GET',
            url: '/api/config/org-states',
            headers: { 'Content-Type': 'application/json' },
        }, authToken)
        .then((response) => {
            _orgStatesCache = response.data?.data || null;
            return _orgStatesCache;
        })
        .catch((err) => {
            console.log('error fetching org states: ', err);
            // Don't poison the cache on failure — let the next caller retry.
            throw err;
        })
        .finally(() => {
            _orgStatesInFlight = null;
        });
    return _orgStatesInFlight;
}

export function clearOrgStatesCache() {
    _orgStatesCache = null;
    _orgStatesInFlight = null;
}

export async function getCountryCodesCached(authToken) {
    const CACHE_KEY = 'countryCodesList';
    const cached = localStorageSlim.get(CACHE_KEY);
    if (cached) {
        return cached;
    }
    try {
        const response = await apiCallWithAuth({
            method: 'GET',
            url: '/api/config/country-codes',
            headers: {
                'Content-Type': 'application/json',
            },
        }, authToken);
        const data = response.data.data;
        localStorageSlim.set(CACHE_KEY, data, { ttl: CACHED_DATA_TTL_SECONDS });
        return data;
    } catch (err) {
        console.log('error fetching country codes: ', err);
        throw err;
    }
}

/**
 * Fetches transaction statuses from /api/config/transaction-statuses, caches in localStorageSlim for 6 hours, and returns the result.
 * Uses the cache if available and not expired.
 * @param {string} authToken - The authentication token to use for the API call.
 * @returns {Promise<any>} The transaction statuses response (from cache or API)
 */
// The status list's cache key. Versioned: bump it when the config's SHAPE
// changes so browsers drop a list cached before the change (v2: listingLabel).
const TRANSACTION_STATUSES_CACHE_KEY = 'transactionStatusesList_v2';

export async function getTransactionStatusesCached(authToken) {
    const CACHE_KEY = TRANSACTION_STATUSES_CACHE_KEY;
    const cached = localStorageSlim.get(CACHE_KEY);
    if (cached) {
        return cached;
    }
    try {
        const response = await apiCallWithAuth({
            method: 'GET',
            url: '/api/config/transaction-statuses',
            headers: {
                'Content-Type': 'application/json',
            },
        }, authToken);
        const data = response.data.data;
        localStorageSlim.set(CACHE_KEY, data, { ttl: CACHED_DATA_TTL_SECONDS });
        return data;
    } catch (err) {
        console.log('error: ', err)
        throw err;
    }
}

// Sync read of the transaction status list cached by getTransactionStatusesCached.
// Returns array of {label, value} or null if the cache isn't populated yet
// (first page load before the async fetch completes).
export const getCachedTransactionStatusesSync = () => {
    const cached = localStorageSlim.get(TRANSACTION_STATUSES_CACHE_KEY);
    return Array.isArray(cached) ? cached : null;
};

/**
 * Resolve a raw transaction status value (e.g. "under_review") to its display
 * label ("Pending") using the backend config. Backend is the source of truth
 * — renaming a label there updates every UI surface that calls this helper.
 *
 * Priority: explicit `statusesOverride` arg, then the synchronous cache read.
 * Falls back to Title-Casing the value if neither is available (first render
 * before the async fetch lands).
 *
 * A LISTING may word a status differently: the config entry's `listingLabel`
 * (e.g. active = "Pending" on a transaction — under contract — but "Active" on
 * a listing, which is on the market). Pass recordType 'listing' to get it;
 * without a listingLabel the transaction label is used.
 *
 * @param {string} value - raw status value from the DB
 * @param {Array<{label:string,value:string,listingLabel?:string}>} [statusesOverride] - pre-fetched list
 * @param {string} [recordType] - 'listing' for a listing; anything else = transaction
 */
export const formatStatusLabel = (value, statusesOverride, recordType) => {
    if (!value) return '—';
    const list = statusesOverride || getCachedTransactionStatusesSync();
    if (Array.isArray(list)) {
        const match = list.find(s => s.value === value);
        if (match && recordType === 'listing' && match.listingLabel) return match.listingLabel;
        if (match?.label) return match.label;
    }
    return String(value)
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
};

/**
 * The status list as a LISTING words it: each entry's label replaced by its
 * listingLabel when the config has one. For screens that only show listings
 * (the Listings page), so its filter, chips and pills all read the same.
 */
export const listingStatusOptions = (list) => (Array.isArray(list) ? list : [])
    .map((s) => ((s && s.listingLabel) ? { ...s, label: s.listingLabel } : s));

/**
 * Fetches the canonical list of transaction phases from /api/config/transaction-phases.
 * Cached in localStorageSlim for 6 hours. Used to populate the phase chip menu
 * and the grid filter. The list is small (7 items) and effectively immutable.
 * @param {string} authToken
 * @returns {Promise<Array<{value:string,label:string,phaseId:string,name:string,systemKey:string,isSystem:boolean,sortOrder:number}>>}
 */
export async function getTransactionPhasesCached(authToken) {
    // Cache key bumped to V2: /config/transaction-phases now returns per-org phase
    // ROWS (snake_case: phase_id/name/is_system/sort_order) instead of the old
    // {label,value} static config, so a pre-existing cache would hold the wrong
    // shape. The bump forces one fresh fetch into the normalized shape below.
    const CACHE_KEY = 'transactionPhasesListV2';
    const cached = localStorageSlim.get(CACHE_KEY);
    if (cached) {
        return cached;
    }
    try {
        const response = await apiCallWithAuth({
            method: 'GET',
            url: '/api/config/transaction-phases',
            headers: {
                'Content-Type': 'application/json',
            },
        }, authToken);
        const raw = (response.data && response.data.data) || [];
        const list = Array.isArray(raw) ? raw : [];
        // Normalize to the {value,label} shape every picker/filter consumes, while
        // carrying phase metadata (id/name/isSystem/sortOrder/systemKey) for any
        // consumer that needs it. transaction_phase now stores the id (value); the
        // display name is the label — callers map id -> name from this list.
        const normalized = list.map((p) => {
            const id = (p && (p.phase_id || p.phaseId || p.value)) || '';
            const name = (p && (p.name || p.label)) || '';
            return {
                value: id,
                label: name,
                phaseId: id,
                name: name,
                systemKey: (p && (p.system_key || p.systemKey)) || '',
                isSystem: Boolean(p && (p.is_system || p.isSystem)),
                sortOrder: (p && (p.sort_order !== undefined ? p.sort_order : p.sortOrder)) || 0,
            };
        });
        localStorageSlim.set(CACHE_KEY, normalized, { ttl: CACHED_DATA_TTL_SECONDS });
        return normalized;
    } catch (err) {
        console.log('error fetching transaction phases: ', err);
        throw err;
    }
}

/**
 * Clears the cached transaction-phases list so the next read re-fetches. Call
 * after an admin mutates phases (create / rename / delete / reorder) so the
 * transactions list + detail pick up the change immediately instead of waiting
 * out the cache TTL.
 */
export function clearTransactionPhasesCache() {
    try {
        localStorageSlim.remove('transactionPhasesListV2');
    } catch (e) {
        // best-effort
    }
}

/**
 * Fetches the org's user list from /api/users, caches in localStorageSlim for
 * 6 hours, and returns the result. Used by surfaces that need to render
 * assignee pickers / filters (transactions list, dashboard home, transaction
 * tasks tab) — they all share the same shape and the list is small + stable
 * within a session.
 *
 * @param {string} authToken
 * @returns {Promise<Array<{userId:string, fullName?:string, userName?:string, email?:string}>>}
 */
export async function getOrgUsersCached(authToken) {
    const CACHE_KEY = 'orgUsersList';
    const cached = localStorageSlim.get(CACHE_KEY);
    if (cached) {
        return cached;
    }
    try {
        const response = await apiCallWithAuth({
            method: 'GET',
            url: '/api/users',
            headers: {
                'Content-Type': 'application/json',
            },
        }, authToken);
        const data = Array.isArray(response.data?.data) ? response.data.data : [];
        localStorageSlim.set(CACHE_KEY, data, { ttl: CACHED_DATA_TTL_SECONDS });
        return data;
    } catch (err) {
        console.log('error fetching org users: ', err);
        return [];
    }
}
// --- Post-confirm checklist-attach marker (Tasks-tab loader gating) ---
// After a transaction moves under_review -> active, its task checklists attach
// ASYNCHRONOUSLY (polling service). The review-confirm flow drops a short-lived
// sessionStorage marker; the Tasks tab reads it to decide whether to show the
// "setting up tasks" loader (and poll audit-logs) vs. load tasks immediately.
// Keyed by transactionId, valid for ATTACH_MARKER_TTL_MS. This keeps the normal
// Tasks-tab experience untouched — no marker => today's immediate load.
const ATTACH_MARKER_TTL_MS = 5 * 60 * 1000;
const attachMarkerKey = (transactionId) => `txnChecklistAttach:${transactionId}`;

export const markChecklistAttachInProgress = (transactionId) => {
    try {
        if (transactionId) sessionStorage.setItem(attachMarkerKey(transactionId), String(Date.now()));
    } catch (e) { /* sessionStorage unavailable — degrade to no loader */ }
};

export const hasFreshChecklistAttachMarker = (transactionId) => {
    try {
        const v = sessionStorage.getItem(attachMarkerKey(transactionId));
        if (!v) return false;
        const ts = parseInt(v, 10);
        return Number.isFinite(ts) && (Date.now() - ts) < ATTACH_MARKER_TTL_MS;
    } catch (e) { return false; }
};

export const clearChecklistAttachMarker = (transactionId) => {
    try { sessionStorage.removeItem(attachMarkerKey(transactionId)); } catch (e) { /* ignore */ }
};

// --- Post-confirm contact-sync marker (Contacts-tab loader gating) ---
// Same shape and reasoning as the checklist-attach marker above. Contacts moved
// out of the synchronous review-save into the polling service (2026-09-04), so
// they now appear a few seconds AFTER the save returns. Without a marker the
// Contacts tab would render an empty list in that window, which reads as "this
// document named nobody" rather than "still working".
//
// Separate key and TTL from the checklist marker on purpose: the two async jobs
// are independent, either can finish first, and one failing must not clear the
// other's loader.
const CONTACT_SYNC_MARKER_TTL_MS = 5 * 60 * 1000;
const contactSyncMarkerKey = (transactionId) => `txnContactSync:${transactionId}`;

export const markContactSyncInProgress = (transactionId) => {
    try {
        if (transactionId) sessionStorage.setItem(contactSyncMarkerKey(transactionId), String(Date.now()));
    } catch (e) { /* sessionStorage unavailable — degrade to no loader */ }
};

export const hasFreshContactSyncMarker = (transactionId) => {
    try {
        const v = sessionStorage.getItem(contactSyncMarkerKey(transactionId));
        if (!v) return false;
        const ts = parseInt(v, 10);
        return Number.isFinite(ts) && (Date.now() - ts) < CONTACT_SYNC_MARKER_TTL_MS;
    } catch (e) { return false; }
};

export const clearContactSyncMarker = (transactionId) => {
    try { sessionStorage.removeItem(contactSyncMarkerKey(transactionId)); } catch (e) { /* ignore */ }
};
